import { UploadPageLayout } from "components/modules/Layout/UploadPageLayout/UploadPageLayout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import type { GetServerSideProps } from "next";
import { Navbar } from "components/modules/Navigation/Navbar/Navbar";
import Head from "next/head";
import { LanguageProvider } from "@/contexts/LanguageProvider";
import { commonLanguageFiles } from "@/config";
import { translateFunc } from "@/utils/translateFunc";
import { Translate } from "components/common/Translate/Translate";
import dynamic from "next/dynamic";
import React from "react";
import { ImageConverterSection3Content } from "components/features/ImageConverter/ImageConverterSection3Content";
import { ImageConverterSection4Tutorial } from "components/features/ImageConverter/ImageConverterSection4Tutorial";
import ImageConverterPreToolsArea from "components/features/ImageConverter/ImageConverterPreToolsArea";
import { TrustPilotSection } from "components/modules/TrustPilot/TrustPilotSection";
import { retry } from "@/utils/retry";
import { PageInfoProvider } from "@/contexts/PageInfoProvider";
import { PageInfo } from "@/modules/ImageConverter/types/PageInfo";
import { getPageInfo } from "@/modules/ImageConverter/queries/getPageInfo";
import { GroupConverterJsonTemplate } from "@/templates/GroupConverterJsonTemplate";
import { useRouter } from "next/router";
import GroupConverterTutorialSVG from "components/common/Resources/GroupConverterTutorialSVG";
import { SecondaryGroupConverterJsonTemplate } from "@/templates/SecondaryGroupConverterJsonTemplate";
import { SingleConverterJsonTemplate } from "@/templates/SingleConverterJsonTemplate";

const ImageConverterUploader = dynamic<{}>(() =>
    import("components/modules/Uploader/Converter/ImageConverterUploader").then((mod) => mod.ImageConverterUploader),
);

interface PageProps {
    pageInfo: PageInfo;
}

const Page = ({ pageInfo }: PageProps) => {
    const router = useRouter();
    const format = getFormat(pageInfo);
    const formatVarObject: Record<string, string> = { format };
    if (pageInfo.template === "Single") {
        formatVarObject.srcFormat = pageInfo.slug.split("-to-")[0].toUpperCase();
        formatVarObject.targetFormat = pageInfo.slug.split("-to-")[1].toUpperCase();
    }
    const template = getJsonTemplate(pageInfo);
    const metaTitle = translateFunc(template.meta.pageTitle, formatVarObject);
    const description = translateFunc(template.meta.metaDescription, formatVarObject);
    const metaKeywords = translateFunc(template.meta.metaKeywords, formatVarObject);
    return (
        <>
            <Head>
                <title>{metaTitle}</title>
                <meta name="description" content={description} />
                <meta name="keywords" content={metaKeywords} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content="/social.png" />
                <meta property="twitter:card" content="summary" />
                <meta property="twitter:title" content={metaTitle} />
                <meta property="twitter:description" content={description} />
            </Head>
            <UploadPageLayout
                navbar={<Navbar url={router.asPath} />}
                title={<Translate keyName={template.section1.section1Title} variables={formatVarObject} />}
                subTitle={<Translate keyName={template.section1.section1SubTitle} variables={formatVarObject} />}
                uploader={<ImageConverterUploader />}
                content={<ImageConverterSection3Content template={template} variables={formatVarObject} />}
                tutorial={
                    <ImageConverterSection4Tutorial
                        template={template}
                        variables={formatVarObject}
                        graphics={<GroupConverterTutorialSVG />}
                    />
                }
                preToolsSlot={<ImageConverterPreToolsArea template={template} variables={formatVarObject} />}
                trustPilotSection={
                    <TrustPilotSection title={template.section11?.title} subTitle={template.section11?.subTitle} />
                }
                pageUrl={router.asPath}
            />
        </>
    );
};

const getFormat = (pageInfo: PageInfo) => {
    if (pageInfo.template === "Single") {
        return pageInfo.slug.split("-to-")[0].toUpperCase();
    } else {
        return pageInfo.slug.replace("-converter", "").toUpperCase();
    }
};

const getJsonTemplate = (pageInfo: PageInfo) => {
    switch (pageInfo.template) {
        case "Secondary": {
            return SecondaryGroupConverterJsonTemplate;
        }
        case "Single": {
            return SingleConverterJsonTemplate;
        }
        default: {
            return GroupConverterJsonTemplate;
        }
    }
};

const files = (pageInfo: PageInfo) => {
    const getPageJson = (): string => {
        switch (pageInfo.template) {
            case "Group": {
                return "group-converter-landing";
            }
            case "Secondary": {
                return "secondary-group-converter-landing";
            }
            case "Single": {
                return "single-converter-landing";
            }
            default: {
                return "group-converter-landing";
            }
        }
    };

    return [getPageJson(), "uploader", ...commonLanguageFiles];
};

const LanguageWrapper = ({ pageInfo }: PageProps) => (
    <PageInfoProvider pageInfo={pageInfo}>
        <LanguageProvider files={files(pageInfo)}>
            <Page pageInfo={pageInfo} />
        </LanguageProvider>
    </PageInfoProvider>
);

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ params, locale, res }) => {
    if (res) {
        res.setHeader("Cache-Control", "public, s-maxage=14400, stale-while-revalidate=59");
    }
    const { "format-converter": formatConverter } = params!;
    if (!formatConverter) throw new Error("format-converter is required");
    const pageInfo = await retry(() => getPageInfo(formatConverter.toString()));

    if (pageInfo.error) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            key: pageInfo.data.slug,
            pageInfo: pageInfo.data,
            ...(await serverSideTranslations(locale ?? "", files(pageInfo.data))),
        },
    };
};

export default LanguageWrapper;
